import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    onSnapshot
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export type UserRole = 'super_admin' | 'admin' | 'user';

export interface AppUser {
    uid: string;
    email: string;
    displayName?: string;
    role: UserRole;
    contractorId?: string; // For contractor users, links to their contractor profile
    isActive: boolean;
    createdAt: string;
    lastLogin?: string;
    invitedBy?: string;
}

const USERS_COLLECTION = 'users';

// Super Admins (Can see everything)
const SUPER_ADMIN_EMAILS = ['daniel@myhorizon.ai', 'app_creator@myhorizon.ai', 'suhail@myhorizon.ai', 'suhailaljafari@gmail.com', 'demo@test.com'];

// Team Admins (Can see/manage their own team)
const TEAM_ADMIN_EMAILS = ['jerry@eagleonellc.com', 'navin.buxani@gmail.com'];

// Get all users (admin only)
export async function getAllUsers(): Promise<AppUser[]> {
    const q = query(collection(db, USERS_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as AppUser));
}

// Real-time subscription to all users
export function subscribeToUsers(callback: (users: AppUser[]) => void): () => void {
    const q = query(collection(db, USERS_COLLECTION), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const users = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as AppUser));
        callback(users);
    });
}

// Get user by UID
export async function getUser(uid: string): Promise<AppUser | null> {
    const docRef = doc(db, USERS_COLLECTION, uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;
    return { uid: docSnap.id, ...docSnap.data() } as AppUser;
}

// Get user by email
export async function getUserByEmail(email: string): Promise<AppUser | null> {
    const q = query(collection(db, USERS_COLLECTION), where('email', '==', email.toLowerCase()));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { uid: doc.id, ...doc.data() } as AppUser;
}

// Create or update user profile
export async function saveUser(user: AppUser): Promise<void> {
    const docRef = doc(db, USERS_COLLECTION, user.uid);
    // Remove undefined fields to prevent Firestore errors
    const cleanedUser = Object.fromEntries(
        Object.entries(user).filter(([_, v]) => v !== undefined)
    );
    await setDoc(docRef, {
        ...cleanedUser,
        email: user.email.toLowerCase(),
    }, { merge: true });
}

// Update user role
export async function updateUserRole(uid: string, role: UserRole): Promise<void> {
    const docRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(docRef, { role });
}

// Link user to contractor
export async function linkUserToContractor(uid: string, contractorId: string): Promise<void> {
    const user = await getUser(uid);
    const isSuper = user && SUPER_ADMIN_EMAILS.includes(user.email);

    // If Super Admin, Keep Super Admin role. Otherwise, default to 'admin' (assuming first link is admin)
    const newRole = isSuper ? 'super_admin' : 'admin';

    const docRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(docRef, {
        contractorId,
        role: newRole as UserRole
    });
}

// Toggle user active status
export async function toggleUserActive(uid: string, isActive: boolean): Promise<void> {
    const docRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(docRef, { isActive });
}

// Delete user profile (doesn't delete Firebase Auth account)
export async function deleteUserProfile(uid: string): Promise<void> {
    const docRef = doc(db, USERS_COLLECTION, uid);
    await deleteDoc(docRef);
}

// Update last login time
export async function updateLastLogin(uid: string): Promise<void> {
    const docRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(docRef, { lastLogin: new Date().toISOString() });
}

// Check if user is super admin
export async function isSuperAdminUser(uid: string): Promise<boolean> {
    const user = await getUser(uid);
    // Double check against whitelist for security
    return user?.role === 'super_admin' || (user?.email ? SUPER_ADMIN_EMAILS.includes(user.email) : false);
}

// Invitation Types
export interface Invitation {
    id?: string;
    email: string;
    role: UserRole;
    contractorId?: string;
    invitedBy: string; // admin uid
    createdAt: string;
    status: 'pending' | 'accepted';
}

const INVITATIONS_COLLECTION = 'invitations';

// Create an invitation
export async function createInvitation(email: string, role: UserRole, invitedBy: string, contractorId?: string): Promise<void> {
    const cleanEmail = email.toLowerCase().trim();

    // Check if user already exists
    const existingUser = await getUserByEmail(cleanEmail);
    if (existingUser) {
        throw new Error('User already exists in the system.');
    }

    // Check if pending invite exists
    const q = query(collection(db, INVITATIONS_COLLECTION), where('email', '==', cleanEmail), where('status', '==', 'pending'));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
        throw new Error('Pending invitation already exists for this email.');
    }

    // Create invite
    const invite: Invitation = {
        email: cleanEmail,
        role,
        contractorId,
        invitedBy,
        createdAt: new Date().toISOString(),
        status: 'pending'
    };

    await setDoc(doc(db, INVITATIONS_COLLECTION, cleanEmail), invite);
}

// Check for and accept pending invites
async function checkPendingInvites(email: string): Promise<Partial<AppUser> | null> {
    const cleanEmail = email.toLowerCase();
    const docRef = doc(db, INVITATIONS_COLLECTION, cleanEmail);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().status === 'pending') {
        const invite = docSnap.data() as Invitation;

        // Mark as accepted
        await updateDoc(docRef, { status: 'accepted', acceptedAt: new Date().toISOString() });

        return {
            role: invite.role,
            contractorId: invite.contractorId,
            invitedBy: invite.invitedBy
        };
    }
    return null;
}

// Initialize user profile on login
export async function initializeAdminUser(uid: string, email: string): Promise<void> {
    const existingUser = await getUser(uid);
    const normalizedEmail = email.toLowerCase();
    const isSuper = SUPER_ADMIN_EMAILS.includes(normalizedEmail);
    const isTeamAdmin = TEAM_ADMIN_EMAILS.includes(normalizedEmail);

    if (existingUser) {
        // Enforce Roles if in whitelist (auto-promote/demote)
        if (isSuper && existingUser.role !== 'super_admin') {
            await updateUserRole(uid, 'super_admin');
            console.log(`Enforced super_admin role for: ${email}`);
        } else if (isTeamAdmin && existingUser.role !== 'admin' && existingUser.role !== 'super_admin') {
            await updateUserRole(uid, 'admin');
            console.log(`Enforced admin role for: ${email}`);
        }
        await updateLastLogin(uid);
        return;
    }

    // --- New User Logic ---

    // 1. Check for Pre-Provisioned User (Seeded with dummy UID)
    const conflictUser = await getUserByEmail(normalizedEmail);

    // 2. Check for Pending Invite
    const inviteData = await checkPendingInvites(normalizedEmail);

    // Determine Role & Data
    let role: UserRole = 'user';
    let contractorId: string | undefined = undefined;
    let invitedBy: string | undefined = undefined;

    if (conflictUser) {
        console.log(`Migrating pre-provisioned user ${email} from ${conflictUser.uid} to ${uid}`);
        // Migrate data from conflict user
        role = conflictUser.role;
        contractorId = conflictUser.contractorId;
        invitedBy = conflictUser.invitedBy;

        // Delete the old "seed" user to prevent duplicates
        await deleteUserProfile(conflictUser.uid);
    } else if (isSuper) {
        role = 'super_admin';
    } else if (isTeamAdmin) {
        role = 'admin';
    } else if (inviteData) {
        role = inviteData.role || 'user';
        contractorId = inviteData.contractorId;
        invitedBy = inviteData.invitedBy;
    }

    // Create the real Auth User document
    await saveUser({
        uid,
        email: normalizedEmail,
        displayName: conflictUser?.displayName || undefined,
        role,
        contractorId,
        invitedBy,
        isActive: true,
        createdAt: conflictUser?.createdAt || new Date().toISOString(),
        lastLogin: new Date().toISOString(),
    });

    console.log(`User initialized: ${email} as ${role} (Merged: ${!!conflictUser})`);
}
